/**
 * Care→Practice Anfragen — Practice Service (Empfangsseite)
 *
 * Praxis empfängt und bearbeitet Anfragen von Pflegeeinrichtungen.
 * Supabase-Tabelle: care_practice_requests
 */
import { supabase, isSupabaseConfigured, requireSupabase } from "@/lib/supabase";
import { logger } from "@/lib/logger";
import { getPartners } from "./network.service";
import {
    buildCarePracticeRequestStats,
    createCarePracticeRequestSubscription,
    mapCarePracticeRequestRow,
    mapTimelineEntry,
} from "@/services/care-practice-request.shared";
import type {
    CarePracticeRequest,
    UpdateRequestInput,
    RequestStats,
    RequestCategory,
    RequestPriority,
    RequestStatus,
    RequestTimelineEntry,
    AppointmentRequestPayload,
} from "@/types/care-practice-request";
import { DEFAULT_LINKING_REQUEST_CATEGORIES } from "@/types/care-practice-request";

type RealtimeDebugEntry = Record<string, unknown>;

function recordRealtimeDebug(entry: RealtimeDebugEntry): void {
    if (typeof window === "undefined") return;
    const target = window as typeof window & {
        __MFRealtimeDebug?: RealtimeDebugEntry[];
    };
    const nextEntry = { ...entry, ts: new Date().toISOString() };
    target.__MFRealtimeDebug = [...(target.__MFRealtimeDebug ?? []).slice(-49), nextEntry];
}

const CARE_REQUEST_ATTACHMENTS_BUCKET = "care-request-attachments" as const;
const CARE_REQUEST_ATTACHMENT_SIGNED_URL_TTL_SECONDS = 900;
const ABSOLUTE_URL_SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

export interface CarePracticeLinkStatus {
    exists: boolean;
    linkId: string | null;
    createdAt: string | null;
    updatedAt: string | null;
}

interface CarePracticeLinkStatusRow {
    id: string | null;
    created_at: string | null;
    updated_at: string | null;
    expires_at: string | null;
    is_active: boolean | null;
    status: string | null;
    source_facility_id: string | null;
    target_facility_id: string | null;
    source_practice_id: string | null;
    target_practice_id: string | null;
}

export interface ResolveLinkingRequestInput {
    requestId: string;
    residentId: string;
    patientId: string;
    facilityId: string;
    practiceId: string;
    categories?: string[];
    lastComment?: string;
    resolutionNotes?: string;
}

function sanitizeCareRequestAttachmentObjectPath(rawPath: string): string | null {
    const trimmedPath = rawPath.trim();
    if (!trimmedPath) return null;

    let normalizedPath = trimmedPath
        .split(/[?#]/, 1)[0]
        ?.replace(/^\/+/, "")
        .replace(/\\/g, "/");

    if (!normalizedPath) return null;

    if (normalizedPath.startsWith(`${CARE_REQUEST_ATTACHMENTS_BUCKET}/`)) {
        normalizedPath = normalizedPath.slice(CARE_REQUEST_ATTACHMENTS_BUCKET.length + 1);
    }

    if (!normalizedPath) return null;

    const decodedSegments: string[] = [];

    for (const segment of normalizedPath.split("/")) {
        if (!segment || segment === "." || segment === "..") {
            return null;
        }

        try {
            const decoded = decodeURIComponent(segment);
            if (!decoded || decoded === "." || decoded === "..") {
                return null;
            }
            decodedSegments.push(decoded);
        } catch {
            return null;
        }
    }

    return decodedSegments.join("/");
}

function extractCareRequestAttachmentPathFromLegacyUrl(rawUrl: string): string | null {
    let parsedUrl: URL;

    try {
        parsedUrl = new URL(rawUrl);
    } catch {
        return null;
    }

    if (parsedUrl.protocol !== "https:") {
        return null;
    }

    const pathname = parsedUrl.pathname;
    const storagePrefixIndex = pathname.indexOf("/storage/v1/");
    const bucketMarker = `/${CARE_REQUEST_ATTACHMENTS_BUCKET}/`;
    const bucketMarkerIndex = pathname.indexOf(bucketMarker);

    if (storagePrefixIndex === -1 || bucketMarkerIndex === -1 || bucketMarkerIndex < storagePrefixIndex) {
        return null;
    }

    const objectPath = pathname.slice(bucketMarkerIndex + bucketMarker.length);
    return sanitizeCareRequestAttachmentObjectPath(objectPath);
}

function normalizeCareRequestAttachmentPath(value: string): string | null {
    const trimmedValue = value.trim();
    if (!trimmedValue) return null;

    const lowerTrimmedValue = trimmedValue.toLowerCase();
    if (lowerTrimmedValue.startsWith("http://") || lowerTrimmedValue.startsWith("https://")) {
        return extractCareRequestAttachmentPathFromLegacyUrl(trimmedValue);
    }

    if (ABSOLUTE_URL_SCHEME_PATTERN.test(trimmedValue)) {
        return null;
    }

    return sanitizeCareRequestAttachmentObjectPath(trimmedValue);
}

// ── DB → TS Mapper ──────────────────────────────────────────

// ── Queries ─────────────────────────────────────────────────

/** Alle Anfragen an diese Praxis laden */
export async function getRequestsForPractice(
    practiceId: string,
    options?: { status?: RequestStatus; category?: RequestCategory; limit?: number },
): Promise<CarePracticeRequest[]> {
    if (!isSupabaseConfigured()) return [];
    try {
        let query = supabase
            .from("care_practice_requests")
            .select("*")
            .eq("practice_id", practiceId)
            .order("created_at", { ascending: false });

        if (options?.status) query = query.eq("status", options.status);
        if (options?.category) query = query.eq("category", options.category);
        if (options?.limit) query = query.limit(options.limit);

        const { data, error } = await query;
        if (error) throw error;
        return (data ?? []).map((r: Record<string, unknown>) => mapCarePracticeRequestRow(r));
    } catch (e) {
        logger.error("careRequests.getForPractice", e);
        return [];
    }
}

/** Einzelne Anfrage laden */
export async function getRequest(
    id: string,
    practiceId?: string,
): Promise<CarePracticeRequest | null> {
    if (!isSupabaseConfigured()) return null;
    try {
        let query = supabase.from("care_practice_requests").select("*").eq("id", id);
        if (practiceId) query = query.eq("practice_id", practiceId);

        const { data, error } = await query.single();
        if (error) throw error;
        return data ? mapCarePracticeRequestRow(data as Record<string, unknown>) : null;
    } catch (e) {
        logger.error("careRequests.getRequest", e);
        return null;
    }
}

/** Private Care-Request-Anhänge als signierte URLs auflösen (z. B. WUNDE-Fotos) */
export async function getCareRequestAttachmentSignedUrls(paths: string[]): Promise<string[]> {
    if (!isSupabaseConfigured()) return [];

    const normalizedPaths = Array.from(
        new Set(
            paths
                .map((value) => normalizeCareRequestAttachmentPath(value))
                .filter((value): value is string => Boolean(value)),
        ),
    );

    if (!normalizedPaths.length) return [];

    try {
        const sb = requireSupabase();
        const urls: string[] = [];

        for (const path of normalizedPaths) {
            const { data, error } = await sb.storage
                .from(CARE_REQUEST_ATTACHMENTS_BUCKET)
                .createSignedUrl(path, CARE_REQUEST_ATTACHMENT_SIGNED_URL_TTL_SECONDS);

            if (error) {
                logger.warn("careRequests.getAttachmentSignedUrl", error.message);
                continue;
            }

            if (data?.signedUrl) {
                urls.push(data.signedUrl);
            }
        }

        return urls;
    } catch (e) {
        logger.error("careRequests.getAttachmentSignedUrls", e);
        return [];
    }
}

/** Request-Status und/oder Kommentar aktualisieren */
export async function updateRequest(
    id: string,
    input: UpdateRequestInput,
    _userId?: string,
    _practiceId?: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb.rpc("transition_care_practice_request", {
            p_request_id: id,
            p_command: input.command,
            p_last_comment: input.lastComment?.trim() || null,
            p_resolution_notes: input.resolutionNotes?.trim() || null,
        });
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error("careRequests.updateRequest", e);
        return false;
    }
}

// ════════════════════════════════════════════════════════════════════════════
// 2026-05-22 Voice Phase 2.11 — Terminwunsch → echter Termin
// ════════════════════════════════════════════════════════════════════════════

/**
 * Consent-gated resident→patient Resolver. Ruft die SECURITY-DEFINER-RPC
 * `resolve_consented_patient_for_resident`. Liefert patient_id NUR bei aktivem
 * Link + erteiltem, gültigem Consent der Praxis — sonst null. KEIN Termin
 * darf ohne diesen patient_id angelegt werden.
 */
export async function resolveConsentedPatientForResident(
    residentId: string,
    practiceId: string,
): Promise<{ patientId: string | null; error: Error | null }> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc("resolve_consented_patient_for_resident", {
            p_resident_id: residentId,
            p_practice_id: practiceId,
        });
        if (error) {
            logger.error("careRequests.resolveConsentedPatientForResident", error.message);
            return { patientId: null, error: new Error(error.message) };
        }
        // RPC gibt uuid ODER null zurück — null ist valide (kein Consent).
        return { patientId: typeof data === "string" ? data : null, error: null };
    } catch (e) {
        logger.error("careRequests.resolveConsentedPatientForResident", e);
        return { patientId: null, error: e instanceof Error ? e : new Error(String(e)) };
    }
}

/**
 * Verknüpft einen erstellten Termin mit dem Pflege-Terminwunsch und schließt
 * die Anfrage ab — über die SECURITY-DEFINER-RPC link_appointment_to_care_request:
 *   - setzt care_practice_requests.payload.appointment_id (JSONB-Strangler)
 *   - Status → ERLEDIGT + care_practice_request_history-Eintrag
 *   - consent-gated: Termin-Patient muss dem resident_patient_link entsprechen
 *
 * P0-A Fix (2026-05-22): vorher direkter Client-UPDATE auf care_practice_requests
 * → „permission denied for table" (authenticated hat KEIN UPDATE-Grant; alle
 * Writes auf der Tabelle laufen über SECDEF-RPCs). Jetzt ein einziger RPC-Call,
 * der payload + Status + History serverseitig atomar erledigt.
 */
export async function linkAppointmentToCareRequest(params: {
    requestId: string;
    appointmentId: string;
    currentPayload: AppointmentRequestPayload;
    resolutionNotes: string;
}): Promise<{ ok: boolean; error: string | null }> {
    try {
        const sb = requireSupabase();
        // currentPayload wird nicht mehr gesendet — die RPC merged
        // payload.appointment_id aus dem Live-DB-Stand.
        const note = params.resolutionNotes.trim().slice(0, 500);
        const { error } = await sb.rpc("link_appointment_to_care_request", {
            p_request_id: params.requestId,
            p_appointment_id: params.appointmentId,
            p_note: note.length > 0 ? note : null,
        });
        if (error) {
            logger.error("careRequests.linkAppointmentToCareRequest", error.message);
            return { ok: false, error: error.message };
        }
        return { ok: true, error: null };
    } catch (e) {
        logger.error("careRequests.linkAppointmentToCareRequest", e);
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
}

/**
 * Führt eine Terminverschiebungs-Anfrage (appointment_reschedule_request) aus —
 * über die SECURITY-DEFINER-RPC reschedule_appointment_from_care_request:
 *   - verschiebt den bestehenden appointment auf payload.requested_new_datetime
 *     (UPDATE — kein neuer Termin, keine Löschung)
 *   - setzt payload.appointment_id, Status ERLEDIGT, History
 *   - consent-gated, Past-Date- + Konflikt-Check serverseitig, atomar
 *
 * Die neue Zeit kommt server-authoritativ aus dem Payload — kein Client-Datum.
 */
export async function executeRescheduleFromCareRequest(params: {
    requestId: string;
    note?: string | null;
}): Promise<{ ok: boolean; error: string | null }> {
    try {
        const sb = requireSupabase();
        const trimmed = (params.note ?? "").trim().slice(0, 500);
        const { error } = await sb.rpc("reschedule_appointment_from_care_request", {
            p_request_id: params.requestId,
            p_note: trimmed.length > 0 ? trimmed : null,
        });
        if (error) {
            logger.error("careRequests.executeRescheduleFromCareRequest", error.message);
            return { ok: false, error: error.message };
        }
        return { ok: true, error: null };
    } catch (e) {
        logger.error("careRequests.executeRescheduleFromCareRequest", e);
        return { ok: false, error: e instanceof Error ? e.message : String(e) };
    }
}

export async function resolveLinkingRequestWithRpc(
    input: ResolveLinkingRequestInput,
): Promise<boolean> {
    try {
        const sb = requireSupabase();

        const { error: linkError } = await sb.rpc("create_practice_authorized_care_link", {
            p_request_id: input.requestId,
            p_resident_id: input.residentId,
            p_patient_id: input.patientId,
            p_facility_id: input.facilityId,
            p_practice_id: input.practiceId,
            p_categories: input.categories?.length
                ? input.categories
                : [...DEFAULT_LINKING_REQUEST_CATEGORIES],
        });

        if (linkError) {
            throw linkError;
        }

        const { error: resolveError } = await sb.rpc("transition_care_practice_request", {
            p_request_id: input.requestId,
            p_command: "resolve",
            p_last_comment: input.lastComment?.trim() || null,
            p_resolution_notes: input.resolutionNotes?.trim() || null,
        });

        if (resolveError) {
            throw resolveError;
        }

        return true;
    } catch (e) {
        logger.error("careRequests.resolveLinkingRequestWithRpc", e);
        return false;
    }
}

export async function getCarePracticeLinkStatus(params: {
    residentId: string;
    patientId: string;
    facilityId: string;
    practiceId: string;
}): Promise<CarePracticeLinkStatus> {
    const emptyState: CarePracticeLinkStatus = {
        exists: false,
        linkId: null,
        createdAt: null,
        updatedAt: null,
    };

    if (!isSupabaseConfigured()) {
        return emptyState;
    }

    try {
        const sb = requireSupabase();
        const { data, error } = await sb
            .from("cross_sector_data_sharing")
            .select([
                "id",
                "created_at",
                "updated_at",
                "expires_at",
                "is_active",
                "status",
                "source_facility_id",
                "target_facility_id",
                "source_practice_id",
                "target_practice_id",
            ].join(", "))
            .eq("resident_id", params.residentId)
            .eq("patient_id", params.patientId)
            .eq("is_active", true);

        if (error) {
            throw error;
        }

        const linkRows = ((data ?? []) as unknown as Array<CarePracticeLinkStatusRow>);
        const now = Date.now();
        const activeLink = linkRows.find((row) => {
            const facilityId = typeof row.source_facility_id === "string"
                ? row.source_facility_id
                : typeof row.target_facility_id === "string"
                    ? row.target_facility_id
                    : null;
            const practiceId = typeof row.source_practice_id === "string"
                ? row.source_practice_id
                : typeof row.target_practice_id === "string"
                    ? row.target_practice_id
                    : null;

            if (facilityId !== params.facilityId) {
                return false;
            }

            if (practiceId !== params.practiceId) {
                return false;
            }

            if (typeof row.status === "string" && row.status !== "active") {
                return false;
            }

            if (row.expires_at == null) {
                return true;
            }

            if (typeof row.expires_at !== "string") {
                return false;
            }

            const expiresAtTimestamp = Date.parse(row.expires_at);
            return Number.isNaN(expiresAtTimestamp) || expiresAtTimestamp > now;
        });

        if (!activeLink) {
            return emptyState;
        }

        return {
            exists: true,
            linkId: (activeLink.id as string) ?? null,
            createdAt: (activeLink.created_at as string) ?? null,
            updatedAt: (activeLink.updated_at as string) ?? null,
        };
    } catch (e) {
        logger.error("careRequests.getCarePracticeLinkStatus", e);
        return emptyState;
    }
}

/** Status-Statistiken für Badge-Anzeige */
export async function getRequestStats(practiceId: string): Promise<RequestStats> {
    const empty: RequestStats = { neu: 0, gesehen: 0, inBearbeitung: 0, rueckfrage: 0, total: 0 };
    if (!isSupabaseConfigured()) return empty;
    try {
        const { data, error } = await supabase
            .from("care_practice_requests")
            .select("status")
            .eq("practice_id", practiceId)
            .neq("status", "ERLEDIGT");
        if (error) throw error;
        return buildCarePracticeRequestStats(data as Record<string, unknown>[] | null | undefined);
    } catch (e) {
        logger.error("careRequests.getStats", e);
        return empty;
    }
}

// ── Linked Facilities ────────────────────────────────────────

export interface LinkedFacility {
    facilityId: string;
    facilityName: string;
}

export async function getLinkedFacilities(practiceId: string): Promise<LinkedFacility[]> {
    const result: LinkedFacility[] = [];
    const seen = new Set<string>();

    // Legacy: cross_sector_data_sharing via RPC
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc('get_linked_facilities_for_practice', {
            p_practice_id: practiceId,
        });
        if (!error) {
            for (const row of data ?? []) {
                const r = row as Record<string, unknown>;
                const fId = r.facility_id as string;
                if (!fId || seen.has(fId)) continue;
                seen.add(fId);
                result.push({
                    facilityId: fId,
                    facilityName: (r.facility_name as string) ?? 'Unbekannte Einrichtung',
                });
            }
        }
    } catch (e) {
        logger.error('careRequests.getLinkedFacilities.legacy', e);
    }

    // Strangler-Fig: Partnership-Tabelle
    try {
        const partnerships = await getPartners(practiceId);
        for (const p of partnerships) {
            if (p.status !== 'AKTIV') continue;
            const fId = p.targetFacilityId ?? p.requesterFacilityId;
            if (!fId || seen.has(fId)) continue;
            seen.add(fId);
            result.push({
                facilityId: fId,
                facilityName: p.partnerName ?? 'Unbekannte Einrichtung',
            });
        }
    } catch (e) {
        logger.error('careRequests.getLinkedFacilities.partners', e);
    }

    return result;
}

// ── Create Request (Practice → Care) ────────────────────────

export async function createRequestToCare(input: {
    practiceId: string;
    facilityId: string;
    patientId?: string;
    residentId?: string;
    category: RequestCategory;
    priority: RequestPriority;
    subject: string;
    payload: Record<string, unknown>;
}): Promise<string | null> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc('create_practice_to_care_request', {
            p_practice_id: input.practiceId,
            p_facility_id: input.facilityId,
            p_patient_id: input.patientId ?? null,
            p_resident_id: input.residentId ?? null,
            p_category: input.category,
            p_priority: input.priority,
            p_subject: input.subject,
            p_payload: input.payload,
        });
        if (error) throw error;
        return data as string;
    } catch (e) {
        logger.error('careRequests.createRequestToCare', e);
        return null;
    }
}

// ── Timeline ────────────────────────────────────────────────

export async function getRequestTimeline(requestId: string): Promise<RequestTimelineEntry[]> {
    try {
        const sb = requireSupabase();
        const { data, error } = await sb.rpc('get_care_practice_request_timeline', {
            p_request_id: requestId,
        });
        if (error) throw error;
        return (data ?? []).map((row: Record<string, unknown>) => mapTimelineEntry(row));
    } catch (e) {
        logger.error('careRequests.getRequestTimeline', e);
        return [];
    }
}

// ── Reply ───────────────────────────────────────────────────

export async function replyToRequest(
    requestId: string,
    replyComment: string,
): Promise<boolean> {
    try {
        const sb = requireSupabase();
        const { error } = await sb.rpc('reply_care_practice_request', {
            p_request_id: requestId,
            p_comment: replyComment,
        });
        if (error) throw error;
        return true;
    } catch (e) {
        logger.error('careRequests.replyToRequest', e);
        return false;
    }
}

/** Realtime-Subscription auf Änderungen */
export function subscribeToRequests(
    practiceId: string,
    onUpdate: () => void,
): { unsubscribe: () => void } {
    if (!isSupabaseConfigured()) return { unsubscribe: () => {} };

    return createCarePracticeRequestSubscription({
        client: supabase,
        scope: "practice",
        scopeId: practiceId,
        onEvent: ({ channelName, filter, payload, table }) => {
            const payloadNew = payload.new ?? null;
            const payloadOld = payload.old ?? null;
            recordRealtimeDebug({
                channelName,
                eventType: payload.eventType ?? null,
                filter,
                kind: "event",
                newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                table,
            });
            logger.debug("careRequests.realtime.event", {
                channelName,
                eventType: payload.eventType ?? null,
                filter,
                newId: typeof payloadNew?.id === "string" ? payloadNew.id : null,
                newStatus: typeof payloadNew?.status === "string" ? payloadNew.status : null,
                oldId: typeof payloadOld?.id === "string" ? payloadOld.id : null,
                oldStatus: typeof payloadOld?.status === "string" ? payloadOld.status : null,
                table,
            });
        },
        onStatus: ({ channelName, error, status }) => {
            recordRealtimeDebug({ channelName, error, kind: "status", status });
            logger.info("careRequests.realtime.status", { channelName, error, status });
        },
        onUpdate,
    });
}
